/**
 * API Field Mapping Repository
 * 
 * Handles field classification, option collection, and approval workflow
 * Integrates with existing quote data to provide field management functionality
 */

import type { 
    ApiFieldDefinition, 
    ApiFieldMapping, 
    ApiQuoteRequest,
    FieldType 
} from '../models/apiTypes';

export interface UnknownFieldSubmission {
    id: string;
    submittedFieldName: string;
    submittedValue: string; // Primary value (most recent or common)
    submittedValues: string[]; // All unique values submitted for this field
    submissionCount: number;
    quoteId: string; // Most recent quote ID (for UI compatibility)
    quoteIds: string[]; // All quotes that used this field
    lastSubmitted: Date;
    needsMapping: boolean;
    // New fields for pending options
    type?: 'newOption' | 'unknownField';
    fieldName?: string; // For new options, this is the known field name
    optionValue?: string; // For new options, this is the new option value
    needsApproval?: boolean;
}

export interface FieldClassificationRequest {
    fieldName: string;
    fieldType: FieldType; // 'variable' | 'fixed'
    displayName?: string;
    description?: string;
    units?: string;
    dataType?: string;
    publish: boolean;
    visible: boolean;
}

export class ApiFieldMappingRepository {
    private unknownFields: Map<string, UnknownFieldSubmission> = new Map();
    private fieldDefinitions: Map<string, ApiFieldDefinition> = new Map();
    private deprecatedFields: Set<string> = new Set();

    /**
     * Process a submitted field and track if unknown
     */
    processSubmittedField(fieldName: string, value: any, quoteId: string): {
        isKnown: boolean;
        isDeprecated: boolean;
        needsApproval: boolean;
    } {
        // Check if field is deprecated
        if (this.deprecatedFields.has(fieldName)) {
            return {
                isKnown: true,
                isDeprecated: true,
                needsApproval: false
            };
        }

        // Check if field is already defined
        const existingDefinition = this.fieldDefinitions.get(fieldName);
        if (existingDefinition) {
            // For fixed fields, check if this is a new option value
            if (existingDefinition.fieldType === 'fixed') {
                const stringValue = String(value);
                const hasThisOption = existingDefinition.options?.includes(stringValue);
                return {
                    isKnown: true,
                    isDeprecated: false,
                    needsApproval: !hasThisOption
                };
            }
            
            return {
                isKnown: true,
                isDeprecated: false,
                needsApproval: false
            };
        }

        // Field is unknown - track it for admin review
        this.trackUnknownField(fieldName, String(value), quoteId);
        
        return {
            isKnown: false,
            isDeprecated: false,
            needsApproval: true
        };
    }

    /**
     * Track an unknown field submission
     */
    private trackUnknownField(fieldName: string, value: string, quoteId: string): void {
        const existing = this.unknownFields.get(fieldName);
        const now = new Date();

        if (existing) {
            // Update existing unknown field
            if (!existing.submittedValues.includes(value)) {
                existing.submittedValues.push(value);
            }
            if (!existing.quoteIds.includes(quoteId)) {
                existing.quoteIds.push(quoteId);
            }
            existing.submissionCount++;
            existing.lastSubmitted = now;
            existing.submittedValue = value; // Update to most recent value
            existing.quoteId = quoteId; // Update to most recent quote
        } else {
            // Create new unknown field entry
            const unknownField: UnknownFieldSubmission = {
                id: `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
                submittedFieldName: fieldName,
                submittedValue: value,
                submittedValues: [value],
                submissionCount: 1,
                quoteId: quoteId,
                quoteIds: [quoteId],
                lastSubmitted: now,
                needsMapping: true
            };

            this.unknownFields.set(fieldName, unknownField);
            console.log(`🆕 FIELD MAPPING: New unknown field tracked: ${fieldName} = "${value}"`);
        }
    }

    /**
     * Get all unknown fields needing approval
     */
    getUnknownFieldsNeedingApproval(): UnknownFieldSubmission[] {
        return Array.from(this.unknownFields.values())
            .filter(field => field.needsMapping)
            .sort((a, b) => b.submissionCount - a.submissionCount); // Sort by usage frequency
    }

    /**
     * Classify a field as variable or fixed
     */
    async classifyField(request: FieldClassificationRequest): Promise<ApiFieldDefinition> {
        const unknownField = this.unknownFields.get(request.fieldName);
        
        if (!unknownField) {
            throw new Error(`Unknown field "${request.fieldName}" not found`);
        }

        // Create field definition based on classification
        let options: string[] | null = null;
        if (request.fieldType === 'fixed') {
            // Use all submitted values as options
            options = [...new Set(unknownField.submittedValues)].sort();
        }

        const fieldDefinition: ApiFieldDefinition = {
            id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
            fieldName: request.fieldName,
            apiFieldName: request.fieldName,
            displayName: request.displayName || request.fieldName,
            description: request.description || `Field created from ${unknownField.submissionCount} submissions`,
            exampleValue: unknownField.submittedValues[0] || null,
            publish: request.publish,
            visible: request.visible,
            units: request.units || null,
            options: options,
            required: false,
            fieldType: request.fieldType,
            dataType: request.dataType || 'string',
            aliases: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            isApproved: true
        };

        // Store the field definition
        this.fieldDefinitions.set(request.fieldName, fieldDefinition);

        // Mark unknown field as approved and remove from unknown fields
        this.unknownFields.delete(request.fieldName);

        console.log(`✅ FIELD MAPPING: Field "${request.fieldName}" classified as ${request.fieldType} with ${options?.length || 0} options`);
        
        return fieldDefinition;
    }

    /**
     * Collect additional values for an existing fixed field
     */
    addOptionToFixedField(fieldName: string, newValue: string): boolean {
        const definition = this.fieldDefinitions.get(fieldName);
        
        if (!definition) {
            throw new Error(`Field definition "${fieldName}" not found`);
        }

        if (definition.fieldType !== 'fixed') {
            throw new Error(`Field "${fieldName}" is not a fixed field`);
        }

        if (!definition.options) {
            definition.options = [];
        }

        const stringValue = String(newValue);
        if (!definition.options.includes(stringValue)) {
            definition.options.push(stringValue);
            definition.options.sort(); // Keep options sorted
            definition.updatedAt = new Date();
            
            console.log(`➕ FIELD MAPPING: Added option "${stringValue}" to fixed field "${fieldName}"`);
            return true;
        }

        return false; // Option already exists
    }

    /**
     * Deprecate a field (no new submissions accepted)
     */
    deprecateField(fieldName: string): void {
        this.deprecatedFields.add(fieldName);
        
        // Update field definition if it exists
        const definition = this.fieldDefinitions.get(fieldName);
        if (definition) {
            definition.publish = false;
            definition.updatedAt = new Date();
        }

        console.log(`⚠️ FIELD MAPPING: Field "${fieldName}" deprecated - no new submissions will be accepted`);
    }

    /**
     * Check if a field is deprecated
     */
    isFieldDeprecated(fieldName: string): boolean {
        return this.deprecatedFields.has(fieldName);
    }

    /**
     * Get all field definitions for API documentation
     */
    getPublishedFieldDefinitions(): ApiFieldDefinition[] {
        return Array.from(this.fieldDefinitions.values())
            .filter(field => field.publish && !this.deprecatedFields.has(field.fieldName))
            .sort((a, b) => a.fieldName.localeCompare(b.fieldName));
    }

    /**
     * Get field statistics
     */
    getFieldStats(): {
        totalDefinitions: number;
        unknownFields: number;
        deprecatedFields: number;
        fixedFields: number;
        variableFields: number;
    } {
        const definitions = Array.from(this.fieldDefinitions.values());
        
        return {
            totalDefinitions: definitions.length,
            unknownFields: this.unknownFields.size,
            deprecatedFields: this.deprecatedFields.size,
            fixedFields: definitions.filter(f => f.fieldType === 'fixed').length,
            variableFields: definitions.filter(f => f.fieldType === 'variable').length
        };
    }

    /**
     * Load field definitions from existing data
     */
    loadFieldDefinitions(definitions: ApiFieldDefinition[]): void {
        this.fieldDefinitions.clear();
        for (const definition of definitions) {
            this.fieldDefinitions.set(definition.fieldName, definition);
        }
        console.log(`📋 FIELD MAPPING: Loaded ${definitions.length} field definitions`);
    }

    /**
     * Analyze existing quotes to populate unknown fields
     */
    analyzeQuotesForUnknownFields(quotes: ApiQuoteRequest[]): void {
        console.log(`🔍 FIELD MAPPING: Analyzing ${quotes.length} quotes for unknown fields`);
        
        for (const quote of quotes) {
            // Extract all field names from quote (excluding standard fields)
            const standardFields = new Set(['id', 'productNumber', 'requestId', 'status', 'isDuplicate', 
                'customerId', 'customerEmail', 'msrpPrice', 'dealerPrice', 'manufacturingPrice', 
                'quantity', 'totalLabourMinutes', 'additionalMiscLabourMinutes', 'createdAt', 
                'updatedAt', 'quotedAt', 'acceptedAt', 'shippedAt', 'userId', 'userEmail', 
                'userName', 'userCompany', 'submittedAt']);

            for (const [fieldName, value] of Object.entries(quote)) {
                if (!standardFields.has(fieldName) && value !== null && value !== undefined) {
                    this.processSubmittedField(fieldName, value, quote.id);
                }
            }
        }

        console.log(`✅ FIELD MAPPING: Found ${this.unknownFields.size} unique unknown fields from quote analysis`);
    }

    /**
     * Reset all data (for testing/development)
     */
    reset(): void {
        this.unknownFields.clear();
        this.fieldDefinitions.clear();
        this.deprecatedFields.clear();
        console.log(`🔄 FIELD MAPPING: Repository reset`);
    }
}

// Singleton instance for application use
export const apiFieldMappingRepository = new ApiFieldMappingRepository();